/**
 * V2StickyContainerSection — Editor for the sticky floating container
 *
 * Layout when enabled:
 * 1. Enable/disable toggle
 * 2. Container-level settings (Appearance + Spacing) — MUI Accordion groups directly
 * 3. Button type dropdown (Button / PurchaseButton)
 * 4. SectionRowCard + InlineSectionEditor — standard section pattern for the button
 *
 * Always anchored to bottom. Exactly one button at a time.
 * Writes via onChange('content.flexibleLayout.stickyContainer', ...)
 */

import React, { useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SAccordion, SAccordionNestingContext, WithGuideHelp } from '@modules/ui-components';
import type { LayoutSection } from '@segmently/schema-models/v2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { StickyContainerConfig } from '@modules/screen-view-v2/types/stickyContainer';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { useEditorGuideService } from '@modules/reactFlow/contexts';
import {
  createDefaultStickyContainer,
  STICKY_BG_COLOR_CONFIG,
  STICKY_OPACITY_CONFIG,
  STICKY_CORNER_RADIUS_CONFIG,
  STICKY_BORDER_WIDTH_CONFIG,
  STICKY_BORDER_COLOR_CONFIG,
} from '../../../config/stickyContainerDefaults';
import { createDefaultSection, type AddMenuKind } from '../../../config/flexibleSectionDefaults';
import { FlexibleSectionContentAdapter } from './FlexibleSectionContentAdapter';
import { SectionRowCard, getSectionDisplayLabel } from './SectionRowCard';
import { StyleFieldRow } from '../../shared/styleFields/StyleFieldRow';
import { PaddingElement, type PaddingValues } from '../../../factories/elements';
import { checkboxSx, checkboxIconSx, checkboxCheckedIconSx } from '../../../config/editorFieldStyles';
import { getSectionTestId } from '@modules/reactFlow/utils/testIdUtils';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Detect purchase buttons by action.kind === 'purchase' */
function isPurchaseButton(section: LayoutSection): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (section as any).buttonContent?.action?.kind === 'purchase';
}

type ButtonType = 'Button' | 'PurchaseButton';

/** Accordion group styling — matches SectionLayoutSettings pattern */
const ACCORDION_GROUP_SX = {
  backgroundColor: 'transparent',
  '&:before': { display: 'none' },
  '& .MuiAccordionSummary-root': {
    minHeight: 32,
    px: 1,
    py: 0,
    backgroundColor: 'action.hover',
    borderRadius: 1,
    '&:hover': { backgroundColor: 'action.selected' },
  },
  '& .MuiAccordionSummary-content': { margin: '4px 0' },
};

// ─── Props ─────────────────────────────────────────────────────────────────

export interface V2StickyContainerSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  /** Project ID for upload service (needed by button content adapter) */
  projectId?: string;
  /** Onboarding ID for upload service */
  onboardingId?: string;
  /** Version ID for upload service */
  versionId?: string;
  /** Available locales for multi-locale upload dialog */
  availableLocales?: string[];
  /** Theme settings for themed defaults when creating button sections */
  themeSettingsV2?: OnboardingThemeSettingsV2 | null;
}

// ─── Component ─────────────────────────────────────────────────────────────

export const V2StickyContainerSection: React.FC<V2StickyContainerSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = false,
  projectId,
  onboardingId,
  versionId,
  availableLocales,
  themeSettingsV2,
}) => {
  // Read stickyContainer from flexibleLayout config
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stickyContainer = ((step.content as any)?.flexibleLayout?.stickyContainer ?? null) as StickyContainerConfig | null;
  const enabled = stickyContainer !== null;

  const basePath = 'content.flexibleLayout.stickyContainer';
  const guideService = useEditorGuideService();

  const renderGuideLabel = (
    guideKey: string,
    label: string,
    variant: 'body2' | 'caption' = 'body2',
  ): React.ReactNode => {
    const labelNode = (
      <Typography variant={variant} color="text.secondary" component="span">
        {label}
      </Typography>
    );

    if (!guideService) return labelNode;

    return (
      <WithGuideHelp
        guideKey={guideKey}
        guideService={guideService}
        data-testid={`guide-anchor-${guideKey}`}
        position="inline-end"
        iconSize="small"
      >
        {labelNode}
      </WithGuideHelp>
    );
  };

  // Main FlexibleLayout sections — used for parent linking in button content adapter
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mainSections = useMemo<LayoutSection[]>(
    () => ((step.content as any)?.flexibleLayout?.sections ?? []) as LayoutSection[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [(step.content as any)?.flexibleLayout?.sections],
  );

  // ── Toggle enable/disable ────────────────────────────────────────────

  const handleToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        onChange(basePath, createDefaultStickyContainer(locale, themeSettingsV2));
      } else {
        onChange(basePath, null);
      }
    },
    [onChange, locale, themeSettingsV2],
  );

  // ── Offset (EdgeInsets) ───────────────────────────────────────────────

  const offsetValues = useMemo<PaddingValues>(() => {
    const o = stickyContainer?.offset;
    return {
      paddingTop: typeof o?.top === 'number' ? o.top : 0,
      paddingRight: typeof o?.end === 'number' ? o.end : 16,
      paddingBottom: typeof o?.bottom === 'number' ? o.bottom : 0,
      paddingLeft: typeof o?.start === 'number' ? o.start : 16,
    };
  }, [stickyContainer?.offset]);

  const handleOffsetChange = useCallback(
    (path: string, value: number) => {
      const translated = path
        .replace('.paddingTop', '.top')
        .replace('.paddingRight', '.end')
        .replace('.paddingBottom', '.bottom')
        .replace('.paddingLeft', '.start');
      onChange(translated, value);
    },
    [onChange],
  );

  // ── Padding (EdgeInsets) ──────────────────────────────────────────────

  const paddingValues = useMemo<PaddingValues>(() => {
    const p = stickyContainer?.padding;
    return {
      paddingTop: typeof p?.top === 'number' ? p.top : 8,
      paddingRight: typeof p?.end === 'number' ? p.end : 16,
      paddingBottom: typeof p?.bottom === 'number' ? p.bottom : 8,
      paddingLeft: typeof p?.start === 'number' ? p.start : 16,
    };
  }, [stickyContainer?.padding]);

  const handlePaddingChange = useCallback(
    (path: string, value: number) => {
      const translated = path
        .replace('.paddingTop', '.top')
        .replace('.paddingRight', '.end')
        .replace('.paddingBottom', '.bottom')
        .replace('.paddingLeft', '.start');
      onChange(translated, value);
    },
    [onChange],
  );

  // ── Single button (sections[0]) ──────────────────────────────────────

  const sections = useMemo(
    () => [...(stickyContainer?.sections ?? [])].sort((a, b) => a.order - b.order),
    [stickyContainer?.sections],
  );

  const currentSection = sections.length > 0 ? sections[0] : null;
  const currentButtonType: ButtonType = currentSection && isPurchaseButton(currentSection)
    ? 'PurchaseButton'
    : 'Button';

  const handleButtonTypeChange = useCallback(
    (event: SelectChangeEvent<ButtonType>) => {
      const newType = event.target.value as AddMenuKind;
      const newSection = createDefaultSection(newType, 0, locale, themeSettingsV2);
      onChange(`${basePath}.sections`, [newSection]);
    },
    [onChange, locale, themeSettingsV2],
  );

  // ── writeSections for InlineSectionEditor ─────────────────────────────

  const writeSections = useCallback(
    (updatedSections: LayoutSection[]) => {
      onChange(`${basePath}.sections`, updatedSections);
    },
    [onChange],
  );

  // ── onDeleteRequest — no-op since sticky always has exactly one button ─

  const handleDeleteRequest = useCallback(() => {
    // Sticky container always has exactly one button — deletion is not allowed.
    // The user switches type via the dropdown instead.
  }, []);

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <SAccordion
      title="Sticky Container"
      collapsible
      defaultExpanded={defaultExpanded}
      data-testid={getSectionTestId('Sticky Container')}
    >
      {/* Enable/disable toggle */}
      <Box sx={{ px: 2, pb: 1 }}>
        <FormControlLabel
          label={renderGuideLabel('screenedit-sticky-container-enable-sticky', 'Enable sticky container')}
          control={
            <Switch
              size="small"
              checked={enabled}
              disabled={disabled}
              onChange={(e) => handleToggle(e.target.checked)}
              data-testid="sticky-container-enable-toggle"
            />
          }
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Floating container for CTA buttons that stays visible while content scrolls
        </Typography>
      </Box>

      {enabled && (
        <>
          {/* ── Container-level settings: Appearance + Spacing ──────── */}
          <Box sx={{ px: 2 }}>
            {/* Appearance group */}
            <Accordion defaultExpanded={false} disableGutters elevation={0} sx={ACCORDION_GROUP_SX} data-testid="sticky-appearance-group">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="caption">Appearance</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Show background toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Checkbox
                    checked={stickyContainer?.showBackground !== false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onChange(`${basePath}.showBackground`, checked);
                      if (checked) {
                        onChange(`${basePath}.backgroundColor`, '#FFFFFF');
                        onChange(`${basePath}.opacity`, 1.0);
                      } else {
                        onChange(`${basePath}.backgroundColor`, undefined);
                        onChange(`${basePath}.opacity`, undefined);
                      }
                    }}
                    disabled={disabled}
                    size="small"
                    sx={checkboxSx}
                    icon={<Box sx={checkboxIconSx} />}
                    checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
                    data-testid="sticky-container-show-background-checkbox"
                  />
                  {renderGuideLabel('screenedit-sticky-container-show-background', 'Show background')}
                </Box>

                {stickyContainer?.showBackground !== false && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <StyleFieldRow
                      config={STICKY_BG_COLOR_CONFIG}
                      value={stickyContainer?.backgroundColor ?? '#FFFFFF'}
                      onChange={(v) => onChange(STICKY_BG_COLOR_CONFIG.path, v)}
                      disabled={disabled}
                    />
                    <StyleFieldRow
                      config={STICKY_OPACITY_CONFIG}
                      value={stickyContainer?.opacity ?? 1.0}
                      onChange={(v) => onChange(STICKY_OPACITY_CONFIG.path, v)}
                      disabled={disabled}
                    />
                    <StyleFieldRow
                      config={STICKY_CORNER_RADIUS_CONFIG}
                      value={stickyContainer?.cornerRadius?.uniform ?? 12}
                      onChange={(v) => onChange(`${basePath}.cornerRadius`, { uniform: v })}
                      disabled={disabled}
                    />
                    <StyleFieldRow
                      config={STICKY_BORDER_WIDTH_CONFIG}
                      value={stickyContainer?.border?.width ?? 0}
                      onChange={(v) => onChange(`${basePath}.border`, { ...stickyContainer?.border, width: v })}
                      disabled={disabled}
                    />
                    <StyleFieldRow
                      config={STICKY_BORDER_COLOR_CONFIG}
                      value={stickyContainer?.border?.color ?? '#000000'}
                      onChange={(v) => onChange(`${basePath}.border`, { ...stickyContainer?.border, color: v })}
                      disabled={disabled}
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Spacing group */}
            <Accordion defaultExpanded={false} disableGutters elevation={0} sx={ACCORDION_GROUP_SX} data-testid="sticky-spacing-group">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="caption">Spacing</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <PaddingElement
                  basePath={`${basePath}.offset`}
                  values={offsetValues}
                  onChange={handleOffsetChange}
                  label="Offset from edges"
                  guideKey="screenedit-sticky-container-offset-from-edges"
                  guideService={guideService ?? undefined}
                  disabled={disabled}
                  defaultExpanded={false}
                  data-testid="sticky-container-offset-from-edges"
                />
                <PaddingElement
                  basePath={`${basePath}.padding`}
                  values={paddingValues}
                  onChange={handlePaddingChange}
                  label="Container padding"
                  guideKey="screenedit-sticky-container-padding"
                  guideService={guideService ?? undefined}
                  disabled={disabled}
                  defaultExpanded={false}
                  data-testid="sticky-container-padding"
                />
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* ── Button type selector ─────────────────────────────── */}
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Box sx={{ mb: 0.75 }}>
              {renderGuideLabel('screenedit-sticky-container-button-type', 'Button type', 'caption')}
            </Box>
            <Select<ButtonType>
              value={currentButtonType}
              onChange={handleButtonTypeChange}
              size="small"
              disabled={disabled}
              fullWidth
              sx={{ '& .MuiSelect-select': { fontSize: '0.8rem', py: 0.75 } }}
              data-testid="sticky-container-button-type-select"
            >
              <MenuItem value="Button">Button</MenuItem>
              <MenuItem value="PurchaseButton">Purchase Button</MenuItem>
            </Select>
          </Box>

          {/* ── Button section card (SectionRowCard + InlineSectionEditor) ── */}
          {currentSection && (
            <SAccordionNestingContext.Provider value={false}>
              <Box sx={{ px: 2, pb: 1 }}>
                <Box sx={{ mb: 0.75 }}>
                  {renderGuideLabel('screenedit-sticky-container-button-content', 'Pinned button content', 'caption')}
                </Box>
                <SectionRowCard
                  section={currentSection}
                  displayLabel={getSectionDisplayLabel(currentSection)}
                  onDeleteRequest={handleDeleteRequest}
                  disabled={disabled}
                  renderExpandedContent={() => (
                    <FlexibleSectionContentAdapter
                      section={currentSection}
                      sections={sections}
                      writeSections={writeSections}
                      locale={locale}
                      baseStep={step}
                      disabled={disabled}
                      projectId={projectId}
                      onboardingId={onboardingId}
                      versionId={versionId}
                      availableLocales={availableLocales}
                      linkableSections={mainSections}
                    />
                  )}
                />
              </Box>
            </SAccordionNestingContext.Provider>
          )}
        </>
      )}
    </SAccordion>
  );
};
